import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShareIcon from '@mui/icons-material/Share';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import { Toolbar, AppBar, IconButton, Stack, Grid, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { BsDot } from 'react-icons/bs';
import { FaLanguage } from 'react-icons/fa';
import { MdInfoOutline } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getJobByJobCode,
  getSubmissionSettings,
  IJob,
  ISubmissionFormSettings,
} from 'wisestep-api';
import {
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsButton,
  IConversationState,
  ICandidateAggregatedData,
  formatLocation,
  WsIconText,
} from 'wisestep-base';

import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';

import ContactDropDownListing from './ContactDropDownListing';
import ShareCandidate from './ShareCandidate';

interface ISubmissionWidgetHeader {
  state: IConversationState;
  aggregation: ICandidateAggregatedData | null;
}

const SubmissionWidgetHeader: FC<ISubmissionWidgetHeader> = ({ state, aggregation }) => {
  const [email, setEmail] = useState<string[]>([]);
  const [job, setJob] = useState<IJob | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [profileUrls, setProfileUrls] = useState<string[]>([]);
  const [shareCandidate, setShareCandidate] = useState<boolean>(false);
  const [settings, setSettings] = useState<ISubmissionFormSettings | null>(null);
  const redirect = useLocation();
  const navigate = useNavigate();
  const candidate = state.candidate;
  const refreshPage = useConversationWidgetStore((state) => state.refreshSource);
  const setRefreshPage = useConversationWidgetStore((state) => state.setRefreshSource);
  const onWidgetClose = useConversationWidgetStore((state) => state.onWidgetClose);
  const onNavigation = useConversationWidgetStore((state) => state.onNavigation);

  useEffect(() => {
    (async () => {
      if (isEmpty(settings)) {
        const req = await getSubmissionSettings();
        setSettings(req.data);
      }
    })();
  }, []);

  useEffect(() => {
    let email: string[] = [];
    if (aggregation?.candidateEntity?.email) {
      email = [candidate.email, ...[aggregation.candidateEntity.email]];
    }
    if (aggregation?.candidateEntity?.phoneNumber) {
      if (candidate.phoneNumber) {
        setPhoneNumbers(
          Array.from(
            new Set([
              candidate.phoneNumber,
              ...[aggregation.candidateEntity.phoneNumber],
              ...aggregation.candidateEntity.alternatePhoneNumbers,
            ])
          )
        );
      } else {
        setPhoneNumbers(
          Array.from(
            new Set([
              aggregation.candidateEntity.phoneNumber,
              ...aggregation.candidateEntity.alternatePhoneNumbers,
            ])
          )
        );
      }
    }
    if (aggregation?.candidateEntity?.profileURLs) {
      setProfileUrls(Array.from(new Set([...aggregation.candidateEntity.profileURLs])));
    }
    if (aggregation?.candidateEntity?.alternateEmails) {
      setEmail(Array.from(new Set([...email, ...aggregation.candidateEntity.alternateEmails])));
    } else if (email.length > 0) {
      setEmail(email);
    }
  }, [candidate.email, candidate.phoneNumber, aggregation]);

  useEffect(() => {
    (async () => {
      if (candidate.jobCode && job == null) {
        const req = await getJobByJobCode(candidate.jobCode);
        setJob(req.data);
      }
    })();
  }, [candidate.jobCode, job]);

  const closeWidget = () => {
    onWidgetClose();
    if (redirect.pathname.indexOf('/candidate-job-board-profile') > -1) {
      navigate('/home');
    } else if (refreshPage) {
      setRefreshPage(false);
      window.location.reload();
    }
  };

  const getExpectedSalary = (): string => {
    const latestExp = candidate;
    let salary = 'Not added';
    let salaryPerAnnum = '';
    if (
      latestExp?.expectedBaseSalary &&
      latestExp?.expectedBaseSalaryTimeSpan &&
      latestExp?.expectedSalaryCurrency
    ) {
      salaryPerAnnum = `${latestExp.expectedSalaryCurrency} ${String(latestExp.expectedBaseSalary)} 
      ${latestExp.expectedBaseSalaryTimeSpan}`;
    }
    salary = salaryPerAnnum;
    return isEmpty(salary) ? 'Not added' : salary;
  };

  return (
    <AppBar sx={{ position: 'relative', bgcolor: WsColors.White }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="close"
          sx={{
            backgroundColor: WsColors.TertiaryShades100,
            height: '28px',
            width: '28px',
            mt: !state.candidate.isInDnc
              ? isEmpty(candidate.currentLocation) &&
                isEmpty(candidate.expectedSalaryCurrency) &&
                isEmpty(candidate.noticePeriodInDays) &&
                isEmpty(candidate.workAuthorizationCategory) &&
                isEmpty(candidate.nationality) &&
                isEmpty(candidate.candidateLanguages)
                ? -4
                : -7
              : 0.6,
          }}
        >
          <CloseIcon onClick={closeWidget} sx={{ fontSize: '20px' }} />
        </IconButton>
        <Grid container xs={11} direction="row" alignItems="center" ml={1}>
          {state.candidate.isInDnc ? (
            <Grid item xs={10}>
              <Box ml={2}>
                <WsIconText
                  textCursor="pointer"
                  popOverText="DNC Candidate"
                  icon={<MdInfoOutline style={{ color: WsColors.Yellow500, fontSize: '2px' }} />}
                  textSize={WsTypoVariants.BODY1}
                  textColor={WsTypoColors.SECONDARY}
                  text="The Candidate is in DNC list"
                />
              </Box>
            </Grid>
          ) : (
            <Grid item xs={10}>
              <Stack direction="row">
                {settings && settings?.name && settings?.name.hidden === false && (
                  <>
                    <Box mt={0.5}>
                      <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                        {candidate?.name ?? ''}
                      </WsTypography>
                    </Box>
                    <BsDot style={{ fontSize: '30px' }} />
                  </>
                )}
                <Box mt={0.5}>
                  <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                    {candidate?.mappedJobTitle ?? ''}
                  </WsTypography>
                </Box>
                <BsDot style={{ fontSize: '30px' }} />
                <Box mt={0.5}>
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                    {candidate?.jobCode ?? ''}
                  </WsTypography>
                </Box>
                {!isEmpty(job?.locations) && job?.locations && job?.locations[0] != undefined && (
                  <>
                    <BsDot style={{ fontSize: '30px' }} />
                    <Box mt={0.5}>
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                        {formatLocation(job?.locations[0])}
                      </WsTypography>
                    </Box>
                  </>
                )}
                {!isEmpty(job?.companyName) && (
                  <>
                    <BsDot style={{ fontSize: '30px' }} />
                    <Box mt={0.5}>
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                        {job?.companyName}
                      </WsTypography>
                    </Box>
                  </>
                )}
              </Stack>
              <Stack direction="row" ml={1.2} mb={0.8} spacing={3}>
                <ContactDropDownListing view="email" data={email} />
                {settings && settings?.phoneNumber && settings?.phoneNumber.hidden === false && (
                  <ContactDropDownListing view="phone" data={phoneNumbers} />
                )}
                {settings && settings?.linkedInURL && settings?.linkedInURL.hidden === false && (
                  <ContactDropDownListing view="social" data={profileUrls} />
                )}
              </Stack>
              <Stack direction="row" ml={1} mb={0.8} spacing={2}>
                {settings &&
                  settings?.currentLocation &&
                  settings?.currentLocation.hidden === false && (
                    <>
                      {!isEmpty(candidate.currentLocation) && (
                        <WsIconText
                          textCursor="pointer"
                          popOverText="Current Location"
                          icon={
                            <LocationOnOutlinedIcon
                              sx={{ color: WsColors.TheBlues500, fontSize: '20px' }}
                            />
                          }
                          textSize={WsTypoVariants.BODY1}
                          textColor={WsTypoColors.SECONDARY}
                          text={formatLocation(candidate.currentLocation)}
                        />
                      )}
                    </>
                  )}
                {settings &&
                  settings?.expectedCompensation &&
                  settings?.expectedCompensation.hidden === false && (
                    <>
                      {!isEmpty(candidate.expectedSalaryCurrency) && (
                        <WsIconText
                          textCursor="pointer"
                          popOverText="Expected Salary"
                          icon={
                            <PaidOutlinedIcon
                              sx={{ color: WsColors.TheBlues500, fontSize: '20px' }}
                            />
                          }
                          textSize={WsTypoVariants.BODY1}
                          textColor={WsTypoColors.SECONDARY}
                          text={getExpectedSalary()}
                        />
                      )}
                    </>
                  )}
                {settings && settings?.noticePeriod && settings?.noticePeriod.hidden === false && (
                  <>
                    {candidate?.noticePeriodInDays != undefined &&
                      candidate.noticePeriodInDays != null && (
                        <WsIconText
                          textCursor="pointer"
                          popOverText="Notice Period"
                          icon={
                            <AccessTimeOutlinedIcon
                              sx={{ color: WsColors.TheBlues500, fontSize: '20px' }}
                            />
                          }
                          textSize={WsTypoVariants.BODY1}
                          textColor={WsTypoColors.SECONDARY}
                          text={
                            candidate?.noticePeriodInDays == 0
                              ? 'Immediately'
                              : `${candidate?.noticePeriodInDays} days`
                          }
                        />
                      )}
                  </>
                )}
                {settings &&
                  settings?.workAuthorizationCategory &&
                  settings?.workAuthorizationCategory.hidden === false && (
                    <>
                      {!isEmpty(candidate.workAuthorizationCategory) &&
                        candidate?.workAuthorizationCategory && (
                          <>
                            {candidate?.workAuthorization ? (
                              <WsIconText
                                textCursor="pointer"
                                popOverText="Work Authorization Category | Work Authorization"
                                icon={
                                  <WorkHistoryOutlinedIcon
                                    sx={{ color: WsColors.TheBlues500, fontSize: '20px' }}
                                  />
                                }
                                textSize={WsTypoVariants.BODY1}
                                textColor={WsTypoColors.SECONDARY}
                                text={
                                  candidate?.workAuthorizationCategory +
                                  ' | ' +
                                  candidate?.workAuthorization
                                }
                              />
                            ) : (
                              <WsIconText
                                textCursor="pointer"
                                popOverText="Work Authorization Category"
                                icon={
                                  <WorkHistoryOutlinedIcon
                                    sx={{ color: WsColors.TheBlues500, fontSize: '20px' }}
                                  />
                                }
                                textSize={WsTypoVariants.BODY1}
                                textColor={WsTypoColors.SECONDARY}
                                text={candidate?.workAuthorizationCategory}
                              />
                            )}
                          </>
                        )}
                    </>
                  )}
                {settings && settings?.visaType && settings?.visaType.hidden === false && (
                  <>
                    {!isEmpty(candidate.visaStatus) && candidate?.visaStatus && (
                      <WsIconText
                        textCursor="pointer"
                        popOverText="Visa status"
                        icon={
                          <ReceiptLongOutlinedIcon
                            sx={{ color: WsColors.TheBlues500, fontSize: '20px' }}
                          />
                        }
                        textSize={WsTypoVariants.BODY1}
                        textColor={WsTypoColors.SECONDARY}
                        text={candidate?.visaStatus}
                      />
                    )}
                  </>
                )}

                {settings && settings?.nationality && settings?.nationality.hidden === false && (
                  <>
                    {!isEmpty(candidate.nationality) && candidate?.nationality && (
                      <WsIconText
                        textCursor="pointer"
                        popOverText="Nationality"
                        icon={<FlagIcon sx={{ color: WsColors.TheBlues500, fontSize: '20px' }} />}
                        textSize={WsTypoVariants.BODY1}
                        textColor={WsTypoColors.SECONDARY}
                        text={candidate?.nationality}
                      />
                    )}
                  </>
                )}

                {settings && settings?.languagesKnown && settings?.languagesKnown.hidden === false && (
                  <>
                    {!isEmpty(candidate.candidateLanguages) && candidate?.candidateLanguages && (
                      <Stack direction="row" ml={1.5} spacing={1}>
                        <FaLanguage
                          title="Languages"
                          style={{ color: WsColors.TheBlues500, fontSize: '26px' }}
                        />
                        {candidate?.candidateLanguages?.map((languages, index: number) => (
                          <Box mt={1} key={index}>
                            <WsTypography
                              hoverText="Languages"
                              bold={true}
                              display="inline"
                              color={WsTypoColors.SECONDARY}
                            >
                              {languages.languageName}
                            </WsTypography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
          )}
          <Grid item container xs={2} direction="row" justifyContent="flex-end">
            <Stack direction="row" spacing={3}>
              <ShareIcon
                onClick={() => setShareCandidate(true)}
                sx={{ color: WsColors.TheBlues500, mt: 1.5, cursor: 'pointer' }}
              />
              <Grid
                display="flex"
                alignItems="center"
                pl={1}
                pt={1}
                gap={1}
                xs={10}
                justifyContent="space-evenly"
              >
                <WsButton
                  variant="outlined"
                  startIcon={<KeyboardArrowLeftIcon />}
                  onClick={() => onNavigation('prev')}
                >
                  Prev
                </WsButton>

                <WsButton
                  variant="outlined"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => onNavigation('next')}
                >
                  Next
                </WsButton>
              </Grid>
            </Stack>
          </Grid>
          {shareCandidate && candidate.id && (
            <ShareCandidate
              interactionId={candidate.id}
              jobTitle={candidate?.mappedJobTitle ?? ''}
              jobCode={candidate.jobCode ?? ''}
              name={candidate?.name}
              onClose={() => setShareCandidate(false)}
              reset={() => {
                setShareCandidate(false);
                setTimeout(() => setShareCandidate(true), 600);
              }}
            />
          )}
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

export default SubmissionWidgetHeader;
